import * as React from "react";

import { useAIChat } from "@/Chat/ChatSidebar/AIChatContext";
import { AssistantToolUseContent } from "@/Chat/ChatSidebar/ChatByIdView/ChatMessage/AssistantToolUseContent.tsx";
import { ChatFeedback } from "@/Chat/ChatSidebar/ChatByIdView/ChatMessage/ChatFeedback";
import { ChatMessageComponents } from "@/Chat/ChatSidebar/ChatByIdView/ChatMessage/ChatMessageComponents.tsx";
import { ToolUseGroup } from "@/Chat/ChatSidebar/ChatByIdView/ChatMessage/ToolUseGroup";
import { ThinkingIndicator } from "@/shared/components";
import {
  ChatMessage,
  ChatMessageStatus,
  ChatRecord,
} from "@/shared/domain/chat";
import {
  type ChatToolUseContent,
  ChatRole,
  ChatStatus,
  isChatTextContent,
  isChatToolResultContent,
  isChatToolUseContent,
} from "@/shared/domain/chat/ChatRecord.ts";

import styles from "./ChatByIdView.module.css";
import { ContextShiftIndicator } from "./ContextShiftIndicator/ContextShiftIndicator";
import { groupToolMessages } from "./groupToolMessages";
import { ScrollToBottomButton } from "./ScrollToBottomButton";
import { useContextShifts } from "./useContextShifts";
import { useScrollSentinel } from "./useScrollSentinel";
import { useSpacer } from "./useSpacer";

const AUTONAV_MAX_AGE_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Check if a message is recent enough for autonav.
 * Messages without a created timestamp (e.g., in-flight) are considered recent.
 */
function isMessageRecentForAutoNav(message: ChatMessage): boolean {
  if (!message.created) {
    return true;
  }
  const messageTime = new Date(message.created).getTime();
  const now = Date.now();
  return now - messageTime < AUTONAV_MAX_AGE_MS;
}

/**
 * Determines if feedback should be shown after a message at the given index.
 * Feedback is shown after assistant messages that end with text content when:
 * 1. The next message is from the user (historical feedback)
 * 2. It's the last message and it's the user's turn (current turn feedback)
 */
function shouldShowFeedback(
  chat: ChatRecord,
  messageIndex: number,
): { show: boolean; lastAssistantText?: string } {
  const message = chat.messages[messageIndex];
  const nextMessage = chat.messages[messageIndex + 1];
  const isLastMessage = messageIndex === chat.messages.length - 1;

  // Only show feedback after assistant messages
  if (message.role !== ChatRole.ASSISTANT) {
    return { show: false };
  }

  // Must have content ending with text
  if (message.content.length === 0) {
    return { show: false };
  }

  const lastContent = message.content[message.content.length - 1];
  if (!isChatTextContent(lastContent)) {
    return { show: false };
  }

  // Show if next message is from user, or if it's the last message and user's turn
  const showAfterHistory = nextMessage && nextMessage.role === ChatRole.USER;
  const showAtEnd = isLastMessage && chat.status === ChatStatus.USER_TURN;

  if (showAfterHistory || showAtEnd) {
    return { show: true, lastAssistantText: lastContent.text };
  }

  return { show: false };
}

interface ChatByIdViewProps {
  chat: ChatRecord;
  isCancelling?: boolean;
  onRetry?: (messageIndex: number) => void;
}

export const ChatByIdView = ({
  chat,
  isCancelling = false,
  onRetry,
}: ChatByIdViewProps) => {
  const { handleLinkClick, autoNavEnabled } = useAIChat();

  // Spacer hook for hoisting user messages to top of viewport
  const { scrollContainerRef, spacerRef, messageToHoistRef, recomputeSpacer } =
    useSpacer();

  // Scroll sentinel hook for detecting when user has scrolled up
  const scrollSentinel = useScrollSentinel(scrollContainerRef);

  // Track user message count to detect when a NEW user message is added
  const userMessageCountRef = React.useRef<number>(0);

  // Track first run to distinguish mount from subsequent updates
  const isFirstRunRef = React.useRef(true);

  // Flag to suppress scroll button flicker during auto-scroll
  const [isAutoScrolling, setIsAutoScrolling] = React.useState(false);

  // Recompute spacer when messages change (streaming, new messages)
  React.useLayoutEffect(recomputeSpacer, [chat.messages, recomputeSpacer]);

  // Handle scroll on mount and when new user messages are added
  React.useLayoutEffect(
    function scrollOnMessagesChange() {
      const currentUserMessageCount = chat.messages.filter(
        (m) => m.role === ChatRole.USER,
      ).length;

      if (isFirstRunRef.current) {
        // First mount: initialize count and scroll to bottom
        isFirstRunRef.current = false;
        userMessageCountRef.current = currentUserMessageCount;

        // Only scroll after browser paint to ensure layout has fully applied
        requestAnimationFrame(function scrollToBottom() {
          scrollSentinel.ref.current?.scrollIntoView({
            block: "end",
            behavior: "instant",
          });
        });

        // Skip the rest on first run
        return;
      }

      if (currentUserMessageCount > userMessageCountRef.current) {
        // New user message: scroll to it with smooth animation
        userMessageCountRef.current = currentUserMessageCount;

        // Suppress button during scroll animation
        setIsAutoScrolling(true);

        messageToHoistRef.current?.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });

        // Clear flag after scroll animation completes (estimate ~300ms)
        setTimeout(() => {
          setIsAutoScrolling(false);
        }, 300);
      }
    },
    [chat.messages, messageToHoistRef, scrollSentinel.ref],
  );

  const thinking = React.useMemo(() => {
    if (chat.messages.length === 0) {
      return true;
    }

    if (chat.status === ChatStatus.USER_TURN) {
      return false;
    }

    const lastMessage = chat.messages.at(-1);

    // If we're currently addressing a tool use request, that has its own loading indicator, so we shouldn't
    // double-display them
    if (lastMessage) {
      const lastContent = lastMessage?.content.at(-1);

      if (isChatToolUseContent(lastContent)) {
        return false;
      }
    }

    return lastMessage?.status === ChatMessageStatus.COMPLETED;
  }, [chat]);

  // Find the index of the last user message for ref attachment
  const lastUserMessageIndex = React.useMemo(() => {
    for (let i = chat.messages.length - 1; i >= 0; i--) {
      if (chat.messages[i].role === ChatRole.USER) {
        return i;
      }
    }
    return -1;
  }, [chat.messages]);

  // Detect context shifts in the message list (incrementally cached)
  const contextShifts = useContextShifts(chat.messages);

  const isGenerating =
    chat.messages.length > 0 && chat.status === ChatStatus.ROBOTO_TURN;

  const messageGroups = React.useMemo(
    () => groupToolMessages(chat.messages),
    [chat.messages],
  );

  const pendingToolUses = React.useMemo((): ChatToolUseContent[] => {
    const lastMessage = chat.messages.at(-1);
    const precedingMessage = chat.messages.at(-2);

    if (
      lastMessage?.role !== ChatRole.ROBOTO ||
      lastMessage.status !== ChatMessageStatus.GENERATING ||
      precedingMessage?.role !== ChatRole.ASSISTANT
    ) {
      return [];
    }

    const completedToolUseIds = new Set(
      lastMessage.content
        .filter(isChatToolResultContent)
        .map((content) => content.tool_use_id),
    );

    return precedingMessage.content
      .filter(isChatToolUseContent)
      .filter((toolUse) => !completedToolUseIds.has(toolUse.tool_use_id));
  }, [chat.messages]);

  return (
    <div ref={scrollContainerRef} className={styles.scrollContainer}>
      <div className={styles.centeringContainer}>
        <div
          className={styles.contentContainer}
          role="log"
          aria-label="Chat conversation"
          aria-live="polite"
        >
          {chat.messages.length > 0 &&
            messageGroups.map((segment) => {
              if (segment.type === "toolGroup") {
                return (
                  <article
                    key={`tool-group-${segment.messageIndices[0]}`}
                    className={styles.messageArticle}
                  >
                    <ToolUseGroup
                      chatId={chat.chat_id}
                      toolResults={segment.toolResults}
                    />
                  </article>
                );
              }

              return segment.messages.map((message, segmentIndex) => {
                const messageIndex = segment.messageIndices[segmentIndex];
                const feedback = shouldShowFeedback(chat, messageIndex);
                const isLastMessage = messageIndex === chat.messages.length - 1;
                const isLastUserMessage = messageIndex === lastUserMessageIndex;

                // Check if this user message has a context shift to show above it
                const shiftContext = contextShifts.get(messageIndex);

                return (
                  <article
                    key={messageIndex}
                    ref={isLastUserMessage ? messageToHoistRef : undefined}
                    className={styles.messageArticle}
                  >
                    {shiftContext && (
                      <ContextShiftIndicator context={shiftContext} />
                    )}
                    <ChatMessageComponents
                      message={message}
                      isLastMessageInChat={isLastMessage}
                      onLinkClick={handleLinkClick}
                      onRetry={
                        onRetry ? () => onRetry(messageIndex) : undefined
                      }
                      autoNavDedupeKey={`${chat.chat_id}-${messageIndex}`}
                      autoNavEnabled={
                        autoNavEnabled && isMessageRecentForAutoNav(message)
                      }
                      isCancelling={isCancelling}
                    />
                    {feedback.show && (
                      <ChatFeedback
                        chatId={chat.chat_id}
                        messageIndex={messageIndex}
                        lastAssistantText={feedback.lastAssistantText}
                      />
                    )}
                  </article>
                );
              });
            })}

          {pendingToolUses.map((toolUse) => (
            <article
              key={`pending-tool-${toolUse.tool_use_id}`}
              className={styles.messageArticle}
            >
              <AssistantToolUseContent toolUse={toolUse} />
            </article>
          ))}

          {/* Thinking indicator */}
          {thinking && <ThinkingIndicator />}

          {/* Scroll sentinel - detects when scrolled to bottom of content */}
          <div
            ref={scrollSentinel.ref}
            aria-hidden="true"
            style={{ height: "1px", width: "100%", pointerEvents: "none" }}
          />

          {/* Spacer at bottom - height set via direct DOM manipulation */}
          <div ref={spacerRef} aria-hidden="true" style={{ height: 0 }} />
        </div>
      </div>

      <div className={styles.scrollToBottomButtonContainer}>
        <ScrollToBottomButton
          show={!scrollSentinel.isVisible && !isAutoScrolling}
          loading={isGenerating}
          onClick={function scrollToBottom() {
            scrollSentinel.ref.current?.scrollIntoView({
              block: "end",
              behavior: "instant",
            });
          }}
        />
      </div>
    </div>
  );
};
